import { addMinutes, format, isBefore, isValid, parseISO } from 'date-fns'

/**
 * Formats a date string while ignoring any timezone component.
 * Use for date-only fields (cpDate, laycanFrom, etc.) to avoid timezone shift issues.
 * "2026-06-01T00:00:00.000Z" → "01-06" (not shifted to May 31 in west timezones)
 */
export function formatDateOnly(
  value: string | undefined | null,
  pattern: string,
): string {
  if (!value) return ''
  const datePart = value.includes('T') ? value.split('T')[0] : value
  return format(parseISO(datePart), pattern)
}

// Filename to be changed after removing the original `dates.ts` file
export function toDateTimeFormat(date: string | Date, pattern: string) {
  return toDateFormat(date, `${pattern}, HH.mm`)
}

export function toDateFormat(dateString: string | Date, pattern: string) {
  if (!dateString) return ''
  const date = dateString instanceof Date ? dateString : new Date(dateString)
  return format(date, pattern)
}

// Returns the difference of months in the future, everything past the startDate returns 0
export function getMonthDiff(startDate: Date, endDate: Date) {
  const yearsDifference = endDate.getFullYear() - startDate.getFullYear()
  const monthsDifference = endDate.getMonth() - startDate.getMonth()
  const daysDifference = endDate.getDate() - startDate.getDate()

  const monthCorrection = daysDifference > 0 ? 1 : 0

  return yearsDifference * 12 + monthsDifference + monthCorrection
}

export function isDateExpired(date?: string) {
  if (!date) return false
  const parsedDate = new Date(date)
  return isBefore(parsedDate, new Date())
}

export function forceDateToZuluTimezone(date: Date) {
  const helperDate = new Date()
  return addMinutes(date, -helperDate.getTimezoneOffset())
}

export const formatDate = (date: string | Date, pattern: string): string => {
  if (!date) return ''
  if (date instanceof Date) return format(date, pattern)
  return format(new Date(date), pattern)
}

/**
 * Parses a date string, trying ISO format first then falling back to Date constructor.
 * Returns Date object (may be invalid if parsing fails).
 */
export function parseDateValue(value: string): Date {
  const parsedIso = parseISO(value)
  return isValid(parsedIso) ? parsedIso : new Date(value)
}

/**
 * Formats a date string with fallback to '-' for empty/invalid values.
 * Uses parseDateValue for robust parsing (ISO first, then Date constructor).
 */
export function formatDateWithFallback(
  value: string | null,
  dateFormat: string,
): string {
  const normalized = value?.trim() ?? ''

  if (!normalized) {
    return '-'
  }

  const parsedDate = parseDateValue(normalized)
  return isValid(parsedDate) ? format(parsedDate, dateFormat) : '-'
}
